import { Injectable, signal, computed, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, tap, map } from 'rxjs/operators';
import { type User, type ProfileUpdateRequest } from '../models/user.model';
import { AUTH_API_URL } from '../config/auth-api-url.token';

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  fullName: string;
  phone?: string;
  city?: string;
  accountType?: 'player' | 'business_owner';
  preferredPosition?: string;
  skillLevel?: 'beginner' | 'intermediate' | 'advanced' | 'professional';
}

export interface AuthResponse {
  user: User;
  token: string;
  refreshToken: string;
  jwt: string;
}

export interface RegisterResponse {
  user: User;
  jwt?: string;
  autoConfirmed?: boolean;
  playerProfileCreated?: boolean;
  phone?: string;
  message: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly TOKEN_KEY = 'auth_token';
  private readonly REFRESH_TOKEN_KEY = 'refresh_token';
  private readonly USER_KEY = 'current_user';
  private readonly REMEMBER_ME_KEY = 'remember_me';
  private readonly apiUrl = inject(AUTH_API_URL, { optional: true }) ?? '/api';

  // Signals for reactive state management
  private readonly _currentUser = signal<User | null>(null);
  private readonly _isAuthenticated = signal<boolean>(false);
  private readonly _isLoading = signal<boolean>(false);

  // Public computed signals
  readonly currentUser = computed(() => this._currentUser());
  readonly isAuthenticated = computed(() => this._isAuthenticated());
  readonly isLoading = computed(() => this._isLoading());
  readonly isAdmin = computed(() => {
    const role = this._currentUser()?.role;
    if (Array.isArray(role)) return role[0]?.type === 'admin';
    return role?.type === 'admin';
  });

  readonly isAuthenticatedRole = computed(() => {
    const role = this._currentUser()?.role;
    if (Array.isArray(role)) return role[0]?.type === 'authenticated';
    return role?.type === 'authenticated';
  });
  //TODO : pending deprecation of isHost.
  readonly isHost = computed(() => {
    const role = this._currentUser()?.role;
    if (Array.isArray(role)) return role[0]?.type === 'host';
    return role?.type === 'host';
  });
  // Role detection handles Strapi v5 array shape (role can be object OR array of objects).
  readonly isGameOrganizerRole = computed(() => {
    const role = this._currentUser()?.role;
    const isOrganizer = (t: string | undefined) => t === 'game_organizer' || t === 'gameOrganizer';
    if (Array.isArray(role)) return role.some(r => isOrganizer(r?.type));
    return isOrganizer(role?.type);
  });

  readonly isBusinessOwnerRole = computed(() => {
    const role = this._currentUser()?.role;
    if (Array.isArray(role)) return role[0]?.type === 'business_owner';
    return role?.type === 'business_owner';
  });

  // ─── Computed signals from PlayerProfile ──────────────────────────

  /**
   * Whether the current user has a playerProfile (i.e. is a player).
   */
  readonly isPlayer = computed(() => !!this._currentUser()?.playerProfile);

  /**
   * Resolves the Position object from playerProfile.
   * Returns the Position reference or null if no profile or no position set.
   */
  readonly preferredPosition = computed(() =>
    this._currentUser()?.playerProfile?.preferredPosition ?? null,
  );

  /**
   * Resolves the SkillLevel object from playerProfile.
   * Returns the SkillLevel reference or null if no profile or no level set.
   */
  readonly skillLevel = computed(() =>
    this._currentUser()?.playerProfile?.skillLevel ?? null,
  );

  /**
   * Resolves the skill level string derived from playerProfile.rating.
   * Returns the derived string or null if no playerProfile.
   */
  readonly skillLevelLabel = computed<string | null>(() => {
    const pp = this._currentUser()?.playerProfile;
    if (pp) return this.deriveSkillLevel(pp.rating);
    return null;
  });

  /**
   * Resolves rating from playerProfile.rating or 0 if no profile.
   */
  readonly rating = computed(() =>
    this._currentUser()?.playerProfile?.rating ?? 0,
  );

  constructor(
    private http: HttpClient
  ) {
    this.initializeAuth();
  }

  // ─── Storage abstraction ────────────────────────────────────────

  private getStorage(persistent: boolean): Storage {
    return persistent ? localStorage : sessionStorage;
  }

  private hasRememberMe(): boolean {
    return localStorage.getItem(this.REMEMBER_ME_KEY) === 'true';
  }

  private hasUserInStorage(): boolean {
    const storage = this.hasRememberMe() ? localStorage : sessionStorage;
    return storage.getItem(this.USER_KEY) !== null;
  }

  private getUserFromStorage(): User | null {
    const storage = this.hasRememberMe() ? localStorage : sessionStorage;
    const raw = storage.getItem(this.USER_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  private setTokens(jwt: string, refreshToken: string, persistent: boolean): void {
    const storage = this.getStorage(persistent);
    storage.setItem(this.TOKEN_KEY, jwt);
    storage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
    // Always persist the flag in localStorage so it survives reloads.
    localStorage.setItem(this.REMEMBER_ME_KEY, String(persistent));
  }

  private setUser(user: User, persistent: boolean): void {
    const storage = this.getStorage(persistent);
    storage.setItem(this.USER_KEY, JSON.stringify(user));
  }

  private clearTokens(): void {
    [sessionStorage, localStorage].forEach(storage => {
      storage.removeItem(this.TOKEN_KEY);
      storage.removeItem(this.REFRESH_TOKEN_KEY);
      storage.removeItem(this.USER_KEY);
    });
    localStorage.removeItem(this.REMEMBER_ME_KEY);
  }

  // ─── Initialization ─────────────────────────────────────────────

  private initializeAuth(): void {
    const token = this.getToken();
    const user = this.getUserFromStorage();

    if (token && user) {
      try {

        // Stale-session check: if the cached user has root-level player fields
        // WITH ACTUAL VALUES (preferredPosition, skillLevel, rating) but no
        // playerProfile key, the session was cached before the API restructured
        // — force re-login.
        // NOTE: We check for truthy values, not just hasOwnProperty, because
        // Strapi spreads null fields (e.g. preferredPosition: null) which would
        // falsely trigger this check for business owners whose playerProfile
        // was deleted from the response.
        // Cast to `any` — these properties don't exist on the typed User but
        // may be present in stale session data from before the API restructured.
        const raw = user as any;
        const hasRootPlayerValues =
          (raw.preferredPosition != null && raw.preferredPosition !== '') ||
          (raw.skillLevel != null && raw.skillLevel !== '') ||
          (raw.rating != null && raw.rating !== 0 && raw.rating !== '');

        if (
          !Object.prototype.hasOwnProperty.call(raw, 'playerProfile') &&
          hasRootPlayerValues
        ) {
          this.logout();
          return;
        }

        this._currentUser.set(user);
        this._isAuthenticated.set(true);
      } catch {
        this.logout();
      }
    } else if (token && this.hasUserInStorage()) {
      // Token exists and user data exists but is corrupt — clean up.
      this.logout();
    }
  }

  login(credentials: LoginRequest, rememberMe?: boolean): Observable<AuthResponse> {
    this._isLoading.set(true);

    return this.http.post<AuthResponse>(`${this.apiUrl}/auth/login`, credentials)
      .pipe(
        tap(response => this.handleAuthSuccess(response, rememberMe)),
        catchError(error => {
          this._isLoading.set(false);
          return throwError(() => error);
        })
      );
  }

  register(userData: RegisterRequest): Observable<RegisterResponse> {
    return this.http.post<RegisterResponse>(`${this.apiUrl}/auth/register`, userData)
      .pipe(
        catchError(error => {
          return throwError(() => error);
        })
      );
  }


  logout(): void {
    this.clearTokens();

    this._currentUser.set(null);
    this._isAuthenticated.set(false);
    this._isLoading.set(false);
  }

  refreshToken(): Observable<AuthResponse> {
    const refreshToken = this.getRefreshToken();
    const persistent = this.hasRememberMe();

    if (!refreshToken) {
      this.logout();
      return throwError(() => new Error('No refresh token available'));
    }

    return this.http.post<AuthResponse>(`${this.apiUrl}/auth/refresh`, { refreshToken })
      .pipe(
        tap(response => this.handleAuthSuccess(response, persistent)),
        catchError(error => {
          this.logout();
          return throwError(() => error);
        })
      );
  }

  resetPassword(email: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.apiUrl}/auth/forgot-password`, { email });
  }

  confirmResetPassword(token: string, newPassword: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.apiUrl}/auth/reset-password`, { token, newPassword });
  }

  verifyEmail(token: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.apiUrl}/auth/verify-email`, { token });
  }

  /**
   * Fetches the authenticated user's profile from the API (GET /users/profile).
   * Updates the reactive currentUser signal with the response.
   * @returns Observable of the user profile
   */
  getProfile(): Observable<User> {
    return this.http.get<{ user: User }>(`${this.apiUrl}/users/profile`)
      .pipe(
        tap(response => {
          this._currentUser.set(response.user);
          this.setUser(response.user, this.hasRememberMe());
        }),
        map(response => response.user),
      );
  }

  /**
   * Updates the authenticated user's profile (PUT /users/profile).
   * Only known fields are sent; unknown fields are silently ignored by the API.
   * @param profileData - Partial profile fields to update
   * @returns Observable of the updated user
   */
  updateProfile(profileData: ProfileUpdateRequest): Observable<User> {
    return this.http.put<{ user: User }>(`${this.apiUrl}/users/profile`, profileData)
      .pipe(
        tap(response => {
          this._currentUser.set(response.user);
          this.setUser(response.user, this.hasRememberMe());
        }),
        map(response => response.user)
      );
  }

  /**
   * Update the avatar URL locally after a successful upload.
   * Persists to sessionStorage so it survives page reload.
   */
  updateAvatarUrl(url: string): void {
    const currentUser = this._currentUser();
    if (!currentUser) return;

    const updatedUser = { ...currentUser, avatar: url };
    this._currentUser.set(updatedUser);
    this.setUser(updatedUser, this.hasRememberMe());
  }

  getToken(): string | null {
    const storage = this.hasRememberMe() ? localStorage : sessionStorage;
    return storage.getItem(this.TOKEN_KEY);
  }

  getRefreshToken(): string | null {
    const storage = this.hasRememberMe() ? localStorage : sessionStorage;
    return storage.getItem(this.REFRESH_TOKEN_KEY);
  }

  resetLoadingState(): void {
    this._isLoading.set(false);
  }

  handleAuthSuccess(response: AuthResponse, persistent = false): void {
    this.setTokens(response.jwt, response.refreshToken ?? '', persistent);
    this.setUser(response.user, persistent);

    this._currentUser.set(response.user);
    this._isAuthenticated.set(true);
    this._isLoading.set(false);
  }

  /**
   * Derives a skill level string from a numeric rating.
   * - 0 to <2.5 → 'beginner'
   * - 2.5 to <3.5 → 'intermediate'
   * - 3.5 to <4.5 → 'advanced'
   * - 4.5 to 5.0 → 'pro'
   * @param rating - Numeric rating (0-5 scale)
   * @returns Skill level string
   */
  private deriveSkillLevel(rating: number): string {
    if (rating >= 4.5) return 'pro';
    if (rating >= 3.5) return 'advanced';
    if (rating >= 2.5) return 'intermediate';
    return 'beginner';
  }
}
